import React, { useEffect, useState } from 'react';
import { getNativeAudienceMembers, loadNativeAudience } from '../../../../api/marketing';
import { getContactSelectionRows } from '../../../../api/dashboardAssistantHistory';
import { Button } from '@/components/ui/button';

/**
 * What an update proposal does to a saved segment's hand-picked people
 * (ADM-203): who is added, removed and kept, against the segment as saved.
 * The names come from reads the user already has (the list they reviewed and
 * the segment's members); nothing here reaches the model.
 */

/** Names shown per line before "and N more". */
export const NAMES_PER_LINE = 5;
// Bounded reads (review of ADM-203, P1): at most this many pages of each.
const NAME_PAGES = 5;
const ROWS_PAGE = 200;

export type HandPickedDiff = { added: string[]; removed: string[]; kept: number };

/** The proposal's people against the saved ones, in the proposal's and the saved order. */
export function handPickedDiff(saved: string[], proposed: string[]): HandPickedDiff {
  const savedSet = new Set(saved);
  const proposedSet = new Set(proposed);
  return {
    added: proposed.filter((id) => !savedSet.has(id)),
    removed: saved.filter((id) => !proposedSet.has(id)),
    kept: saved.filter((id) => proposedSet.has(id)).length,
  };
}

/** "+3 added, −1 removed, 12 kept": a zero added or removed is left out; kept always shows. */
export function diffSummary({ added, removed, kept }: { added: number; removed: number; kept: number }) {
  return [
    ...(added > 0 ? [`+${added.toLocaleString('en-US')} added`] : []),
    ...(removed > 0 ? [`−${removed.toLocaleString('en-US')} removed`] : []),
    `${kept.toLocaleString('en-US')} kept`,
  ].join(', ');
}

/** "Jane Doe, Omar Ali and 7 more": up to five names, then how many more. */
export function nameLine(names: string[], total: number) {
  const shown = names.slice(0, NAMES_PER_LINE);
  const more = total - shown.length;
  return more > 0 ? `${shown.join(', ')} and ${more.toLocaleString('en-US')} more` : shown.join(', ');
}

type Names = Map<string, string>;

/** "Plus 12 hand-picked people": the line a card shows without a diff. */
function countLine(proposed: string[], hybrid: boolean) {
  return proposed.length ? `${hybrid ? 'Plus ' : ''}${proposed.length.toLocaleString('en-US')} hand-picked ${proposed.length === 1 ? 'person' : 'people'}` : 'No contacts selected.';
}

/**
 * "No one added or removed · 12 hand-picked people kept": a keep update's
 * one line (ADM-223). It says outright that no one was added or removed, so
 * a list the user meant to use is never silently dropped (review of #4692,
 * F3; owner, 2026-10-20).
 */
export function keptLine(count: number) {
  return `No one added or removed · ${count.toLocaleString('en-US')} hand-picked ${count === 1 ? 'person' : 'people'} kept`;
}

/**
 * Names for the added and removed people. The reviewed list has everyone an
 * add or a remove names; a replace's removed people are not on it, so they
 * come from the segment's saved hand-picked members.
 */
async function loadNames({ diff, organizationId, threadId, selectionToken, segmentId }: { diff: HandPickedDiff; organizationId: string; threadId: string; selectionToken: string; segmentId: string }): Promise<Names> {
  const names: Names = new Map();
  const addedWanted = new Set(diff.added);
  const removedWanted = new Set(diff.removed);
  const enough = (set: Set<string>) => [...set].filter((id) => names.has(id)).length >= Math.min(set.size, NAMES_PER_LINE);
  for (let page = 0, cursor = 0; page < NAME_PAGES && !(enough(addedWanted) && enough(removedWanted)); page += 1) {
    const rows = await getContactSelectionRows({ organizationId, threadId, token: selectionToken, cursor, limit: ROWS_PAGE });
    for (const row of rows.rows) {
      if (addedWanted.has(row.id) || removedWanted.has(row.id)) names.set(row.id, row.fullName || row.email || 'Unnamed contact');
    }
    if (rows.done || rows.rows.length === 0) break;
    cursor = rows.nextCursor;
  }
  let membersCursor: string | undefined;
  for (let page = 0; page < NAME_PAGES && !enough(removedWanted); page += 1) {
    const members = await getNativeAudienceMembers({ segmentId, staticOnly: true, ...(membersCursor ? { cursor: membersCursor } : {}) });
    for (const member of members.members) {
      if (removedWanted.has(member.id) && !names.has(member.id)) names.set(member.id, member.name || member.email || 'Unnamed contact');
    }
    if (!members.nextCursor) break;
    membersCursor = members.nextCursor;
  }
  return names;
}

/**
 * Where the diff stands, for the card's Confirm: ready once the counts are
 * shown, which is what the user confirms (Greptile P1 on #4575).
 */
export type HandPickedReviewStatus = 'loading' | 'ready' | 'stale' | 'failed';

type ReviewState =
  | { status: 'idle' }
  | { status: 'stale' }
  | { status: 'failed' }
  | { status: 'diff'; diff: HandPickedDiff; names: Names | null; namesFailed: boolean };

export function HandPickedChangeReview({
  segmentId,
  expectedRevision,
  proposed,
  hybrid,
  selectionToken,
  threadId,
  organizationId,
  live,
  onStatus,
  keepSaved = false,
}: {
  segmentId: string;
  expectedRevision: string | undefined;
  proposed: string[];
  hybrid: boolean;
  selectionToken: string | undefined;
  threadId: string;
  organizationId: string | null | undefined;
  /** No outcome yet and not expired: only a card someone can still confirm loads anything (P1). */
  live: boolean;
  /** Told how the diff stands, so the card turns Confirm on only once the change is shown. */
  onStatus?: (status: HandPickedReviewStatus) => void;
  /**
   * The update keeps the saved hand-picked people exactly (keepSavedPeople,
   * ADM-223): nothing to diff, so nothing loads and the card shows the count
   * and how many are kept. The save's revision check still rejects a stale one.
   */
  keepSaved?: boolean;
}) {
  const [state, setState] = useState<ReviewState>({ status: 'idle' });
  // Bumped by "Try again" after the saved segment failed to load.
  const [attempt, setAttempt] = useState(0);
  const proposedKey = proposed.join(',');
  useEffect(() => {
    if (keepSaved || !live || state.status !== 'idle') return undefined;
    let current = true;
    onStatus?.('loading');
    (async () => {
      let saved: Awaited<ReturnType<typeof loadNativeAudience>>;
      try {
        saved = await loadNativeAudience(segmentId);
      } catch {
        // Never fall back to the count silently: the user would confirm a
        // change they were not shown (Greptile, PR #4524).
        if (current) { setState({ status: 'failed' }); onStatus?.('failed'); }
        return;
      }
      if (!current) return;
      // A builder edit since the draft would read as people "removed"; the
      // diff is shown only against the revision the proposal was made from (S4).
      if (saved.revision !== expectedRevision) { setState({ status: 'stale' }); onStatus?.('stale'); return; }
      const savedIds = saved.definition.kind === 'fixed' || saved.definition.kind === 'hybrid' ? saved.definition.contactIds : [];
      const diff = handPickedDiff(savedIds, proposed);
      setState({ status: 'diff', diff, names: null, namesFailed: false });
      // The counts are what the user confirms; names that are still loading
      // or fail to load never hold Confirm back.
      onStatus?.('ready');
      if (diff.added.length === 0 && diff.removed.length === 0) return;
      try {
        if (!organizationId || !selectionToken) throw new Error('No list to read names from.');
        const names = await loadNames({ diff, organizationId, threadId, selectionToken, segmentId });
        if (current) setState({ status: 'diff', diff, names, namesFailed: false });
      } catch {
        if (current) setState({ status: 'diff', diff, names: null, namesFailed: true });
      }
    })().catch(() => undefined);
    return () => { current = false; };
    // proposedKey stands for proposed; the diff is loaded once per live card.
  }, [keepSaved, live, segmentId, expectedRevision, proposedKey, selectionToken, threadId, organizationId, attempt]);

  if (keepSaved) {
    return <div className="assistant-hand-picked-change">
      <p className="assistant-hand-picked-summary">{keptLine(proposed.length)}</p>
    </div>;
  }

  const plus = hybrid ? 'Plus hand-picked people' : 'Hand-picked people';
  if (state.status === 'stale') {
    return <p className="assistant-hand-picked-stale" role="status">This segment changed after the proposal was prepared. Ask for a fresh proposal.</p>;
  }
  if (state.status === 'failed') {
    return <div className="assistant-hand-picked-change">
      <p>{countLine(proposed, hybrid)}</p>
      <p className="assistant-hand-picked-note" role="status">Couldn’t check what this changes. Try again, or Review in editor shows everyone.</p>
      <div><Button type="button" variant="neutral" size="workspace" onClick={() => { setState({ status: 'idle' }); setAttempt((value) => value + 1); }}>Try again</Button></div>
    </div>;
  }
  if (state.status !== 'diff') {
    // While a live card's change loads, Confirm is off; say why (owner,
    // 2026-10-18).
    if (live) {
      return <div className="assistant-hand-picked-change">
        <p>{countLine(proposed, hybrid)}</p>
        <p className="assistant-hand-picked-note" role="status">Checking what this changes…</p>
      </div>;
    }
    // Until the saved segment loads, and on a card no one can confirm: the count.
    return <p>{countLine(proposed, hybrid)}</p>;
  }
  const { diff, names, namesFailed } = state;
  const named = (ids: string[]) => ids.filter((id) => names?.has(id)).map((id) => names!.get(id)!);
  return <div className="assistant-hand-picked-change">
    <p className="assistant-hand-picked-summary"><span className="assistant-hand-picked-label">{plus}:</span>{' '}
      {/* Each count stays on one line: "12 kept", never "12 / kept". */}
      {diffSummary({ added: diff.added.length, removed: diff.removed.length, kept: diff.kept }).split(', ').map((part, index, parts) => <span key={part} className="assistant-hand-picked-count">{part}{index < parts.length - 1 ? ', ' : ''}</span>)}</p>
    {names && diff.added.length > 0 && named(diff.added).length > 0 && <p className="assistant-hand-picked-names">Added: {nameLine(named(diff.added), diff.added.length)}</p>}
    {names && diff.removed.length > 0 && named(diff.removed).length > 0 && <p className="assistant-hand-picked-names">Removed: {nameLine(named(diff.removed), diff.removed.length)}</p>}
    {namesFailed && <p className="assistant-hand-picked-note">Names couldn’t load. Review in editor shows everyone.</p>}
    {hybrid && diff.removed.length > 0 && <p className="assistant-hand-picked-note">People who match the rules stay in the segment.</p>}
  </div>;
}
